/**
 * verdict.js — 把 analyze() 的结构化事实组织成纯规则断语。
 *
 * 本模块不重算月破、旬空或动变。伏神出伏、多用神取主与历史启发权重只组合
 * analyze() 已给出的爻、日月、marks、interactions、patterns 和五行字段。
 */

import { combines, seasonalState, TRIPLE_COMBINE } from './analyze.js';
import { BRANCH_ELEMENT } from './ganzhi.js';
import { generates, overcomes } from './hexagram.js';
import { timingKeyPoints } from './timing.js';
import { weightValue } from './verdict-weights.js';

/**
 * 卦象层加权依据：
 * - 六冲主散，故减分；六合主合，故加分。变卦只表示后势，权重取本卦一半。
 * - 六冲卦六爻安静，另减 0.5，取“事散不成”；六合卦六爻安静，另加 0.5，
 *   取“稳定持久”。有动爻时不追加安静卦权重。
 * - 三合局已由 analyze() 确认“地支齐全且含动爻”；局对用神与局对世爻
 *   分两笔独立加权，与三刑并见时也各自计分，不自设优先级。依据
 *   《增删卜易·六合章第十九》：“三合其局者必要世爻在局者为美，若世爻
 *   不在于局内，须要局生世爻为吉，局克世爻为凶”。
 * - 反吟主反复，减 1；伏吟主滞，减 0.75。这里只消费 analyze() 的 pattern，
 *   不在语言层重新识别卦象。
 * - “六冲变六合”“六合变六合”按《六合章第十九》先结构化列出，本轮权重
 *   固定为 0，待古卦例输出核定后再决定是否加权。
 */
const PATTERN_WEIGHT_IDS = Object.freeze({
  本卦六冲: 'six-clash-primary',
  变卦六冲: 'six-clash-changed',
  本卦六合: 'six-combine-primary',
  变卦六合: 'six-combine-changed',
  六冲变六合: 'six-clash-to-combine',
  六合变六合: 'six-combine-to-combine',
  爻反吟: 'refrain',
  爻伏吟: 'repetition'
});

/** 伏神路径只使用这里列明的四项权重，不沿用飞神 score。 */
const HIDDEN_WEIGHT_IDS = Object.freeze({
  用神不上卦: 'hidden-absent',
  飞神克伏神: 'flying-overcomes-hidden',
  飞神生伏神: 'flying-generates-hidden',
  伏神可出伏: 'hidden-can-release'
});

const contribution = (id, pattern) => ({ id, pattern, weight: weightValue(id) });

const STATE_RANK = Object.freeze({ 旺: 5, 相: 4, 休: 3, 囚: 2, 死: 1, 平: 0 });
const EMPTY_OR_BROKEN = new Set(['旬空', '月破', '日破']);
const VERDICT_NOTE = '本引擎只排盘与提取断卦要素，吉凶断语需由人依传统断法裁定。';
const HEURISTIC_NOTE = '工程启发值，仅作回归测试锚点；开发集回测的方向一致率低于多数类基线，不可作为吉凶依据。';

const round = (number) => Math.round(number * 100) / 100;
const lineRef = (line) => `${line.position}爻${line.relative}${line.ganzhi}`;
const lineRefWithRole = (line) => `${lineRef(line)}${line.isShi ? '（世爻）' : line.isYing ? '（应爻）' : ''}`;
const displayMark = (line, mark) => mark === '月破' && line.monthBreakSeverity
  ? `月破(${line.monthBreakSeverity})`
  : mark === '空而不空' ? '旬空(空而不空)' : mark;
const marksText = (line) => line.marks.length
  ? line.marks.map((mark) => displayMark(line, mark)).join('、')
  : '无附加标记';
const isSound = (line) => !line.marks.some((mark) => EMPTY_OR_BROKEN.has(mark));

function selectionReason(main, auxiliary) {
  if (main.isShi !== auxiliary.isShi) return '临世';
  if (main.moving !== auxiliary.moving) return '发动';
  if (STATE_RANK[main.state] !== STATE_RANK[auxiliary.state]) {
    return `旺衰较强（${main.state}胜${auxiliary.state}）`;
  }
  if (isSound(main) !== isSound(auxiliary)) return '不空不破';
  return '爻位较低';
}

/** 按临世、动静、旺衰、空破、爻位依次选唯一主爻。 */
function selectMainUseGod(lines) {
  const ordered = [...lines].sort((a, b) => {
    if (a.isShi !== b.isShi) return Number(b.isShi) - Number(a.isShi);
    if (a.moving !== b.moving) return Number(b.moving) - Number(a.moving);
    if (STATE_RANK[a.state] !== STATE_RANK[b.state]) return STATE_RANK[b.state] - STATE_RANK[a.state];
    if (isSound(a) !== isSound(b)) return Number(isSound(b)) - Number(isSound(a));
    return a.position - b.position;
  });
  return {
    main: ordered[0],
    auxiliaries: ordered.slice(1),
    reason: ordered.length > 1 ? selectionReason(ordered[0], ordered[1]) : null
  };
}

function tripleCombineWeights(pattern, result, useElement) {
  const element = /^三合([木火土金水])局$/.exec(pattern)?.[1];
  if (!element || !useElement) return [];

  const weights = [];
  if (element === useElement) {
    weights.push(contribution('triple-same-use-element', `${pattern}同用神五行`));
  } else if (generates(element, useElement)) {
    weights.push(contribution('triple-generates-use', `${pattern}生用神`));
  } else if (overcomes(element, useElement)) {
    weights.push(contribution('triple-overcomes-use', `${pattern}克用神`));
  } else if (generates(useElement, element)) {
    weights.push(contribution('use-generates-triple', `用神生${pattern}`));
  }

  const world = result.yao.find(({ isShi }) => isShi);
  const group = TRIPLE_COMBINE.find((entry) => entry.element === element);
  if (!world || !group) return weights;
  if (group.branches.includes(world.branch)) {
    weights.push(contribution('world-in-triple', `${pattern}世爻在局`));
  } else if (generates(element, world.element)) {
    weights.push(contribution('triple-generates-world', `${pattern}生世爻`));
  } else if (overcomes(element, world.element)) {
    weights.push(contribution('triple-overcomes-world', `${pattern}克世爻`));
  }
  return weights;
}

function patternWeights(result, useElement) {
  const weights = result.patterns.flatMap((pattern) => {
    if (Object.hasOwn(PATTERN_WEIGHT_IDS, pattern)) {
      return [contribution(PATTERN_WEIGHT_IDS[pattern], pattern)];
    }
    return tripleCombineWeights(pattern, result, useElement);
  });
  if (result.moving.length === 0 && result.patterns.includes('本卦六冲')) {
    weights.push(contribution('quiet-six-clash', '六冲卦六爻安静'));
  }
  if (result.moving.length === 0 && result.patterns.includes('本卦六合')) {
    weights.push(contribution('quiet-six-combine', '六合卦六爻安静'));
  }
  return weights;
}

function punishmentWeights(main) {
  const weights = [];
  if (main.isFullPunish) {
    weights.push(contribution('full-punish', `三刑全见(${main.fullPunishGroup})`));
  }
  if (main.isPunished) {
    weights.push(contribution('pair-punish', '两支相刑'));
  }
  return weights;
}

function flyingRelation(hidden, flying) {
  if (generates(flying.element, hidden.element)) {
    const id = HIDDEN_WEIGHT_IDS.飞神生伏神;
    return { id, text: '飞神生伏神，伏神得助', weight: weightValue(id) };
  }
  if (overcomes(flying.element, hidden.element)) {
    const id = HIDDEN_WEIGHT_IDS.飞神克伏神;
    return { id, text: '飞神克伏神，伏神受制', weight: weightValue(id) };
  }
  if (flying.element === hidden.element) return { text: '飞伏同五行，比和相扶', weight: 0 };
  if (generates(hidden.element, flying.element)) return { text: '伏神生飞神，伏神有泄', weight: 0 };
  if (overcomes(hidden.element, flying.element)) return { text: '伏神克飞神，伏神有耗', weight: 0 };
  return { text: '飞伏未见生克', weight: 0 };
}

function dayRelation(hidden, dayGanzhi) {
  const dayBranch = dayGanzhi.at(-1);
  const dayElement = BRANCH_ELEMENT[dayBranch];
  if (hidden.branch === dayBranch) return `${dayGanzhi}日临伏神本支`;
  if (generates(dayElement, hidden.element)) return `${dayGanzhi}日${dayElement}生${hidden.element}`;
  if (overcomes(dayElement, hidden.element)) return `${dayGanzhi}日${dayElement}克${hidden.element}`;
  if (generates(hidden.element, dayElement)) return `伏神${hidden.element}生${dayGanzhi}日${dayElement}而泄`;
  if (overcomes(hidden.element, dayElement)) return `伏神${hidden.element}克${dayGanzhi}日${dayElement}而耗`;
  return `${dayGanzhi}日与伏神${hidden.element}比和`;
}

/**
 * 出伏严格按五项判定：伏神旺相、伏神得日月生扶、飞神空破、飞神被动爻
 * 冲克、伏神被动爻生扶。飞神 score 不参与。
 */
function hiddenRelease(result, hidden, flying) {
  const monthBranch = result.pillars.月.at(-1);
  const dayBranch = result.pillars.日.at(-1);
  const monthElement = BRANCH_ELEMENT[monthBranch];
  const dayElement = BRANCH_ELEMENT[dayBranch];
  const state = seasonalState(hidden.element, monthBranch);
  const reasons = [];

  if (state === '旺' || state === '相') reasons.push(`伏神${hidden.element}在${monthBranch}月为${state}`);
  if (hidden.branch === monthBranch || monthElement === hidden.element || generates(monthElement, hidden.element)) {
    reasons.push('得月建生扶');
  }
  if (hidden.branch === dayBranch || dayElement === hidden.element || generates(dayElement, hidden.element)) {
    reasons.push('得日辰生扶');
  }

  const weakMarks = flying.marks.filter((mark) => EMPTY_OR_BROKEN.has(mark));
  if (weakMarks.length) reasons.push(`飞神见${weakMarks.join('、')}`);

  const attacks = result.interactions.filter(({ to, type }) =>
    to === flying.position && (type === '冲' || type === '克'));
  if (attacks.length) {
    reasons.push(`飞神被${attacks.map(({ from }) => lineRef(result.yao[from - 1])).join('、')}冲克`);
  }

  const supporters = result.yao.filter((line) =>
    line.moving && (line.element === hidden.element || generates(line.element, hidden.element)));
  if (supporters.length) reasons.push(`伏神得${supporters.map(lineRef).join('、')}生扶`);

  const blocked = ['休', '囚', '死'].includes(state)
    && (flying.state === '旺' || flying.state === '相')
    && weakMarks.length === 0
    && attacks.length === 0;
  return {
    state,
    canRelease: reasons.length > 0,
    reasons,
    text: reasons.length
      ? `${reasons.join('；')}，能出伏`
      : blocked
        ? `伏神在${monthBranch}月为${state}且未得日月或动爻生扶，飞神为${flying.state}又不空不破，伏而不出`
        : `伏神未得日月或动爻生扶，飞神不空不破且未受动爻冲克，暂未见出伏条件`
  };
}

function hiddenOutcome(hidden, relation, release) {
  const reason = release.reasons.join('、');
  if (relation.id === HIDDEN_WEIGHT_IDS.飞神克伏神 && release.canRelease) {
    return `飞神克伏神而受制；同时${reason}，满足出伏条件`;
  }
  if (release.canRelease) return `${relation.text}；${reason}，满足出伏条件`;
  return `${relation.text}；${release.text}`;
}

function actorMeaning(source, useGod) {
  if (source.relative === useGod.原神) return '原神发动';
  if (source.relative === useGod.忌神) return '忌神发动';
  if (source.relative === useGod.仇神) return '仇神发动';
  if (source.relative === useGod.用神) return '同类用神发动';
  return `${source.relative}发动`;
}

const MONTH_STATE_TEXT = Object.freeze({
  旺: '得令有力',
  相: '得月建生扶',
  休: '休而力减',
  囚: '无气',
  死: '受月令所制',
  平: '力量平常'
});

function calendarInfluenceText(result, main, subject = '用神', includeAnchor = true) {
  const monthBranch = result.pillars.月.at(-1);
  const dayGanzhi = result.pillars.日;
  const dayBranch = dayGanzhi.at(-1);
  const dayElement = BRANCH_ELEMENT[dayBranch];
  const monthText = `${monthBranch}月${main.element}${main.state}，${subject}${MONTH_STATE_TEXT[main.state]}`;
  let dayText;

  if (main.marks.includes('日墓')) {
    dayText = `${dayGanzhi}日${main.element}墓于${dayBranch}，${subject}入墓`;
  } else if (main.marks.includes('日绝')) {
    dayText = `${dayGanzhi}日${main.element}绝于${dayBranch}，${subject}入绝`;
  } else if (main.branch === dayBranch) {
    dayText = `${dayGanzhi}日临${subject}本支，得日辰扶持`;
  } else if (generates(dayElement, main.element)) {
    dayText = `${dayGanzhi}日${dayElement}生${main.element}，日辰生扶${subject}`;
  } else if (overcomes(dayElement, main.element)) {
    dayText = `${dayGanzhi}日${dayElement}克${main.element}，日辰克制${subject}`;
  } else if (generates(main.element, dayElement)) {
    dayText = `${dayGanzhi}日${subject}${main.element}生${dayElement}，泄于日辰`;
  } else if (overcomes(main.element, dayElement)) {
    dayText = `${dayGanzhi}日${subject}${main.element}克${dayElement}，耗力于日辰`;
  } else {
    dayText = `${dayGanzhi}日与${subject}${main.element}比和`;
  }
  const anchor = includeAnchor ? `就${lineRef(main)}看，` : '';
  return `${result.moving.length ? '先看日月' : '卦无动爻，专看日月'}。${anchor}${monthText}；${dayText}。`;
}

function interactionText(result, main, useGod) {
  const incoming = result.interactions.filter(({ to }) => to === main.position);
  const outgoing = result.interactions.filter(({ from }) => from === main.position);

  const dynamic = [];
  if (incoming.length) {
    dynamic.push(incoming.map(({ from, type }) => {
      const source = result.yao[from - 1];
      const activity = source.moving ? '动而' : source.marks.includes('暗动') ? '暗动而' : '';
      return `${lineRef(source)}${activity}${type}${lineRef(main)}，${actorMeaning(source, useGod)}`;
    }).join('；'));
  }

  const grouped = new Map();
  for (const interaction of outgoing) {
    const targets = grouped.get(interaction.type) || [];
    targets.push(result.yao[interaction.to - 1]);
    grouped.set(interaction.type, targets);
  }
  const effects = { 生: '，泄气', 克: '，耗力', 冲: '，事情走向见冲', 合: '，事情走向见合' };
  if (grouped.size) {
    dynamic.push([...grouped].map(([type, targets]) =>
      `用神${lineRef(main)}${type}${targets.map(lineRef).join('、')}${effects[type] || ''}`).join('；'));
  }

  const calendar = calendarInfluenceText(result, main);
  return dynamic.length ? `${calendar}${dynamic.join('。')}。` : calendar;
}

function transformText(result, main) {
  const anchorText = main.moving
    ? (() => {
      const transformMarks = main.marks.filter((mark) => mark === main.transform?.kind || mark.startsWith('化'));
      return `${lineRef(main)}为动爻，动变标记为${transformMarks.join('、') || main.transform?.kind || '已动'}`;
    })()
    : `${lineRef(main)}安静${result.moving.length ? `；全卦另有动爻${result.moving.map((position) => lineRef(result.yao[position - 1])).join('、')}` : '；全卦六爻安静'}`;
  const patternText = result.patterns.length
    ? `；卦象事实为${result.patterns.join('、')}`
    : '；未见额外卦象模式';
  return `${anchorText}${patternText}。`;
}

function lineEvidence(line) {
  if (!line) return null;
  return {
    position: line.position,
    relative: line.relative,
    ganzhi: line.ganzhi,
    branch: line.branch,
    element: line.element,
    state: line.state,
    moving: Boolean(line.moving),
    isShi: Boolean(line.isShi),
    isYing: Boolean(line.isYing),
    transform: line.transform ? {
      to: line.transform.to,
      toRelative: line.transform.toRelative,
      toBranch: line.transform.toBranch,
      toElement: line.transform.toElement,
      kind: line.transform.kind,
      toVoid: line.transform.toVoid,
      toState: line.transform.toState
    } : null
  };
}

function specialStates(line) {
  const states = [];
  if (line.isVoid) states.push(line.marks?.includes('空而不空') ? '旬空(空而不空)' : '旬空');
  if (line.isMonthBreak) states.push(`月破${line.monthBreakSeverity ? `(${line.monthBreakSeverity})` : ''}`);
  if (line.isDayBreak) states.push('日破');
  if (line.isTomb) states.push('入墓');
  if (line.isGrave) states.push('入绝');
  if (line.isFullPunish) states.push(`三刑全见${line.fullPunishGroup ? `(${line.fullPunishGroup})` : ''}`);
  if (line.transform) {
    states.push(`动化${line.transform.toRelative}${line.transform.to}(${line.transform.kind})`);
    if (line.transform.toVoid) states.push('化空');
    if (line.marks?.includes('化墓')) states.push('化墓');
    if (line.marks?.includes('化绝')) states.push('化绝');
  }
  return [...new Set(states)];
}

function restrainersOf(result, sourcePosition) {
  return result.interactions
    .filter(({ to, type }) => to === sourcePosition && type === '克')
    .map(({ from }) => result.yao[from - 1])
    .filter(Boolean);
}

function relationFacts(result, useLine, hiddenInfo) {
  const overcome = [];
  const generated = [];
  const append = (source, type, sourceKind = source.moving ? '动爻' : '暗动爻') => {
    const restrainers = restrainersOf(result, source.position);
    if (type === '克') {
      overcome.push({
        from: lineEvidence(source),
        target: lineEvidence(useLine),
        sourceKind,
        是否有救: restrainers.length > 0,
        救神: restrainers.map(lineEvidence)
      });
    } else if (type === '生') {
      generated.push({
        from: lineEvidence(source),
        target: lineEvidence(useLine),
        sourceKind,
        是否受制: restrainers.length > 0,
        制约者: restrainers.map(lineEvidence)
      });
    }
  };

  if (hiddenInfo) {
    if (overcomes(hiddenInfo.flying.element, useLine.element)) append(hiddenInfo.flying, '克', '飞神');
    if (generates(hiddenInfo.flying.element, useLine.element)) append(hiddenInfo.flying, '生', '飞神');
  } else {
    for (const { from, to, type } of result.interactions) {
      if (to === useLine.position && (type === '生' || type === '克')) {
        append(result.yao[from - 1], type);
      }
    }
  }
  return { overcome, generated };
}

function calendarKeyPoint(result, useLine, subject) {
  const monthBranch = result.pillars.月.at(-1);
  const dayGanzhi = result.pillars.日;
  const dayBranch = dayGanzhi.at(-1);
  const dayElement = BRANCH_ELEMENT[dayBranch];
  const anchor = subject === '伏神' ? `伏神${lineRef(useLine)}` : lineRefWithRole(useLine);
  const month = useLine.branch === monthBranch
    ? `${monthBranch}月临月建，${useLine.element}${useLine.state}，${subject}${MONTH_STATE_TEXT[useLine.state]}`
    : `${monthBranch}月${useLine.element}${useLine.state}，${subject}${MONTH_STATE_TEXT[useLine.state]}`;
  let day;
  if (useLine.isTomb || useLine.marks?.includes('日墓')) {
    day = `${dayGanzhi}日${useLine.element}墓于${dayBranch}，${subject}入墓`;
  } else if (useLine.isGrave || useLine.marks?.includes('日绝')) {
    day = `${dayGanzhi}日${useLine.element}绝于${dayBranch}，${subject}入绝`;
  } else if (useLine.branch === dayBranch) {
    day = `${dayGanzhi}日临日辰本支，得日辰扶持`;
  } else if (generates(dayElement, useLine.element)) {
    day = `${dayGanzhi}日${dayElement}生${useLine.element}，日辰生扶${subject}`;
  } else if (overcomes(dayElement, useLine.element)) {
    day = `${dayGanzhi}日${dayElement}克${useLine.element}，日辰克制${subject}`;
  } else if (generates(useLine.element, dayElement)) {
    day = `${dayGanzhi}日${subject}${useLine.element}生${dayElement}，泄于日辰`;
  } else if (overcomes(useLine.element, dayElement)) {
    day = `${dayGanzhi}日${subject}${useLine.element}克${dayElement}，耗力于日辰`;
  } else {
    day = `${dayGanzhi}日与${subject}${useLine.element}比和`;
  }
  const evidence = [];
  if (subject === '用神') evidence.push('为本卦主用神');
  if (useLine.god) evidence.push(`六神为${useLine.god}`);
  if (useLine.marks?.length) {
    evidence.push(`爻上标记为${useLine.marks.map((mark) => displayMark(useLine, mark)).join('、')}`);
  }
  return `${anchor}：${evidence.length ? `${evidence.join('；')}；` : ''}${month}；${day}；十二长生为${useLine.changShengStage}。`;
}

function relationKeyPoints(result, relations, useGod) {
  const points = [];
  for (const relation of relations.overcome) {
    const source = result.yao[relation.from.position - 1];
    const sourceText = lineRefWithRole(source);
    const targetText = `${relation.target.position}爻${relation.target.relative}${relation.target.ganzhi}`;
    const activity = relation.sourceKind === '飞神'
      ? '为飞神，'
      : relation.sourceKind === '动爻' ? '发动' : '暗动';
    const meaning = relation.sourceKind === '飞神' ? '飞神克伏神' : actorMeaning(source, useGod);
    const rescue = relation.救神.length
      ? `有${relation.救神.map((item) => `${item.position}爻${item.relative}${item.ganzhi}`).join('、')}发动克制${sourceText}`
      : `卦中未见动爻克制${sourceText}`;
    points.push(`${sourceText}${activity}克用神${targetText}，${meaning}；${rescue}。`);
  }
  for (const relation of relations.generated) {
    const source = result.yao[relation.from.position - 1];
    const sourceText = lineRefWithRole(source);
    const targetText = `${relation.target.position}爻${relation.target.relative}${relation.target.ganzhi}`;
    const activity = relation.sourceKind === '飞神'
      ? '为飞神，'
      : relation.sourceKind === '动爻' ? '发动' : '暗动';
    const meaning = relation.sourceKind === '飞神' ? '飞神生伏神' : actorMeaning(source, useGod);
    const restraint = relation.制约者.length
      ? `但受${relation.制约者.map((item) => `${item.position}爻${item.relative}${item.ganzhi}`).join('、')}发动克制`
      : '卦中未见动爻克制此生扶者';
    points.push(`${sourceText}${activity}生用神${targetText}，${meaning}；${restraint}。`);
  }
  return points;
}

function patternKeyPoint(result, useLine) {
  const pairRefs = () => [[0, 3], [1, 4], [2, 5]].map(([a, b]) =>
    `${lineRef(result.yao[a])}与${lineRef(result.yao[b])}`).join('、');
  const clauses = [];
  for (const pattern of result.patterns) {
    const triple = /^三合([木火土金水])局$/.exec(pattern);
    if (triple) {
      const group = TRIPLE_COMBINE.find(({ element }) => element === triple[1]);
      const members = result.yao.filter(({ branch }) => group?.branches.includes(branch));
      const relations = [];
      if (triple[1] === useLine.element) relations.push(`与用神${lineRef(useLine)}同五行`);
      else if (generates(triple[1], useLine.element)) relations.push(`生用神${lineRef(useLine)}`);
      else if (overcomes(triple[1], useLine.element)) relations.push(`克用神${lineRef(useLine)}`);
      else if (generates(useLine.element, triple[1])) relations.push(`受用神${lineRef(useLine)}所生`);

      const world = result.yao.find(({ isShi }) => isShi);
      if (world && group?.branches.includes(world.branch)) relations.push(`世爻${lineRef(world)}在局中`);
      else if (world && generates(triple[1], world.element)) relations.push(`生世爻${lineRef(world)}`);
      else if (world && overcomes(triple[1], world.element)) relations.push(`克世爻${lineRef(world)}`);
      clauses.push(`${members.map(lineRef).join('、')}成${pattern}${relations.length ? `，${relations.join('、')}` : ''}`);
    } else if (pattern === '本卦六冲' || pattern === '本卦六合') {
      clauses.push(`${pairRefs()}构成${pattern}`);
    } else if (pattern === '变卦六冲' || pattern === '变卦六合') {
      const changedRefs = result.yao.map((line) => line.transform
        ? `${lineRef(line)}变${line.transform.toRelative}${line.transform.to}`
        : lineRef(line));
      clauses.push(`${changedRefs.join('、')}对应${pattern}`);
    } else if (pattern === '爻反吟' || pattern === '爻伏吟') {
      const kind = pattern.slice(1);
      const affected = result.yao.filter((line) => line.transform?.kind === kind);
      clauses.push(`${affected.map((line) => `${lineRef(line)}变${line.transform.toRelative}${line.transform.to}`).join('、')}见${pattern}`);
    } else if (pattern === '六爻安静') {
      clauses.push(`以${lineRef(useLine)}为用神断点，全卦六爻安静`);
    } else if (pattern === '乱动') {
      clauses.push(`${result.moving.map((position) => lineRef(result.yao[position - 1])).join('、')}发动，构成乱动`);
    } else {
      clauses.push(`以${lineRef(useLine)}为用神断点，见${pattern}`);
    }
  }
  for (const relation of result.tripleCombineRelations || []) {
    clauses.push(`${relation.from}${relation.type}${relation.to}`);
  }
  const context = result.question ? `所问“${result.question}”；` : '';
  return clauses.length ? `${context}卦象事实：${clauses.join('；')}。` : null;
}

function compoundKeyPoints(result) {
  const actorText = (actor) => actor.position
    ? `${actor.position}爻${actor.relative}${actor.ganzhi}`
    : `${actor.source}${actor.ganzhi}`;
  const points = [];
  for (const fact of result.compoundFacts || []) {
    const target = result.yao[fact.target.position - 1];
    if (!target) continue;
    if (fact.type === '克处逢生') {
      points.push(
        `${lineRefWithRole(target)}受${fact.overcomeBy.map(actorText).join('、')}克，`
        + `同时得${fact.generatedBy.map(actorText).join('、')}生，构成克处逢生。`
      );
    } else if (fact.type === '破而逢合') {
      const prefix = `${lineRefWithRole(target)}被${fact.brokenBy.source}${fact.brokenBy.ganzhi}冲为月破`
        + `${fact.monthBreakSeverity ? `(${fact.monthBreakSeverity})` : ''}，六合支为${fact.combineBranch}`;
      if (fact.combinedBy.length) {
        points.push(`${prefix}；当前又得${fact.combinedBy.map(actorText).join('、')}合，构成破而逢合。`);
      } else {
        points.push(`${prefix}；当前未见日月或动爻合入。`);
      }
    } else if (fact.type === '元神发动而用神无气') {
      const conditions = [];
      if (fact.conditions.isMonthBreak) {
        conditions.push(`${fact.conditions.month.branch}月月破`);
      }
      if (fact.conditions.isDayOvercome) {
        conditions.push(`${fact.conditions.day.ganzhi}日${BRANCH_ELEMENT[fact.conditions.day.branch]}克`);
      }
      points.push(
        `${fact.originalSpirits.map((actor) => `元神${actorText(actor)}`).join('、')}发动生用神${lineRef(target)}，`
        + `然用神${target.branch}${target.element}${conditions.join('、')}，生之不起。`
      );
    }
  }
  return points;
}

/**
 * 化退神的时序陈述。《增删卜易·进神退神章第二十九》指出退神不能脱离
 * 旺衰与所问远近理解。本层只陈述原问题已明确给出的时间范围，不猜测类别，
 * 也不改变 transform-retreat 的历史启发权重。
 */
function retreatTimingKeyPoints(result) {
  const points = [];
  for (const line of result.yao) {
    if (line.transform?.kind !== '化退神') continue;
    if (line.state === '旺' || line.state === '相') {
      if (result.timeHorizon === 'near') {
        points.push(
          `${lineRefWithRole(line)}发动变${line.transform.toRelative}${line.transform.to}，`
          + `化退神，然爻${line.state}，近事暂不退。`
        );
      }
    } else if (['休', '囚', '死'].includes(line.state)) {
      points.push(
        `${lineRefWithRole(line)}发动变${line.transform.toRelative}${line.transform.to}，`
        + `化退神，爻${line.state}而退。`
      );
    }
  }
  return points;
}

/** 本卦六爻未出现的五行；只在所缺五行恰为用神原神时说明“无生助之源”。 */
function missingElementKeyPoints(result, useLine) {
  const missing = result.elementPresence?.missing || [];
  if (!missing.length) return [];
  const useElement = result.useGod.用神五行 || useLine.element;
  const generatingElement = ['木', '火', '土', '金', '水']
    .find((element) => generates(element, useElement));
  return missing.map((element) => element === generatingElement
    ? `卦中无${element}，用神${lineRef(useLine)}无生助之源。`
    : `卦中无${element}。`);
}

/** 爻与爻的六合是盘上可核事实；两静为合起，任一爻动为合绊，不由此裁决吉凶。 */
function lineCombinationKeyPoints(result) {
  return (result.lineCombinations || []).map(({ first, second, combineState, kind }) => {
    const firstLine = result.yao[first.position - 1];
    if (kind === '动变作合') {
      return `${lineRefWithRole(firstLine)}发动变${second.relative}${second.ganzhi}，`
        + `地支${first.branch}${second.branch}作合，动而逢合为合绊。`;
    }
    const secondLine = result.yao[second.position - 1];
    const stateText = combineState === '合绊'
      ? `${[firstLine, secondLine].filter(({ moving }) => moving).map(lineRefWithRole).join('、')}发动，有一爻动为合绊`
      : '两爻皆静为合起';
    return `${lineRefWithRole(firstLine)}与${lineRefWithRole(secondLine)}地支${first.branch}${second.branch}作合，${stateText}。`;
  });
}

function generalFactKeyPoints(result, useLine) {
  const points = [];
  const activeUsePosition = result.useGod.伏藏 ? null : useLine.position;
  const salientMarks = new Set([
    '临月建', '临日辰', '暗动', '月破', '日破', '旬空', '空而不空',
    '日合', '月合', '合起', '合绊', '日生', '月生', '日克', '月克', '日刑', '月刑', '日害'
  ]);

  for (const line of result.yao) {
    const states = specialStates(line);
    const marks = line.marks.filter((mark) => salientMarks.has(mark));
    const shouldState = line.position !== activeUsePosition
      && (line.isShi || line.moving || line.isStaticClashedByDay || states.length || marks.length);
    if (!shouldState) continue;
    const clauses = [`旺衰为${line.state}`, `六神为${line.god}`];
    if (line.relative === result.useGod.原神) clauses.push('对用神为原神');
    if (line.relative === result.useGod.忌神) clauses.push('对用神为忌神');
    if (line.relative === result.useGod.仇神) clauses.push('对用神为仇神');
    if (line.position !== useLine.position && generates(line.element, useLine.element)) {
      clauses.push(`五行生用神${lineRef(useLine)}`);
    } else if (line.position !== useLine.position && overcomes(line.element, useLine.element)) {
      clauses.push(`五行克用神${lineRef(useLine)}`);
    }
    if (marks.length) clauses.push(`标记为${marks.map((mark) => displayMark(line, mark)).join('、')}`);
    if (states.length) clauses.push(`特殊状态为${states.join('、')}`);
    if (line.isStaticClashedByDay) {
      clauses.push(`被日辰${result.pillars.日}冲，属于静爻逢冲`);
    }
    if (line.combineState) {
      const sources = line.combinedBy.map((actor) => actor.position
        ? `${actor.position}爻${actor.relative}${actor.ganzhi}`
        : `${actor.source}${actor.ganzhi}`);
      clauses.push(`${sources.join('、')}合之，${line.combineState === '合绊' ? '动静之间作合' : '静爻之间作合'}为${line.combineState}`);
    }
    if (line.transform) {
      const monthBranch = result.pillars.月.at(-1);
      const dayBranch = result.pillars.日.at(-1);
      const transformedCalendar = [];
      if (line.transform.toBranch === monthBranch) transformedCalendar.push('变爻临月建');
      if (line.transform.toBranch === dayBranch) transformedCalendar.push('变爻临日辰');
      if (generates(BRANCH_ELEMENT[monthBranch], line.transform.toElement)) transformedCalendar.push('月建生变爻');
      if (overcomes(BRANCH_ELEMENT[monthBranch], line.transform.toElement)) transformedCalendar.push('月建克变爻');
      if (generates(BRANCH_ELEMENT[dayBranch], line.transform.toElement)) transformedCalendar.push('日辰生变爻');
      if (overcomes(BRANCH_ELEMENT[dayBranch], line.transform.toElement)) transformedCalendar.push('日辰克变爻');
      clauses.push(
        `发动后变${line.transform.toRelative}${line.transform.to}`
        + `（${line.transform.toState}${line.transform.toVoid ? '、化空' : '、不空'}），动变类型为${line.transform.kind}`
        + `${transformedCalendar.length ? `；${transformedCalendar.join('、')}` : ''}`
      );
    }
    points.push(`${lineRefWithRole(line)}：${clauses.join('；')}。`);
  }

  for (const interaction of result.interactions) {
    const source = result.yao[interaction.from - 1];
    const target = result.yao[interaction.to - 1];
    const activity = source.moving ? '发动' : '暗动';
    if (interaction.activity === '暗动' || (!source.moving && source.marks.includes('暗动'))) {
      const dayBranch = result.pillars.日.at(-1);
      const dayElement = BRANCH_ELEMENT[dayBranch];
      const reason = source.state === '旺' || source.state === '相'
        ? `爻${source.state}为暗动`
        : dayElement === source.element
          ? `得日辰${result.pillars.日}${dayElement}比扶为暗动`
          : `得日辰${result.pillars.日}${dayElement}生扶为暗动`;
      points.push(
        `${lineRefWithRole(source)}静而逢日辰${result.pillars.日}冲，${reason}，`
        + `暗动${interaction.type}${lineRefWithRole(target)}。`
      );
    } else {
      points.push(`${lineRefWithRole(source)}${activity}${interaction.type}${lineRefWithRole(target)}。`);
    }
    if (target.isShi && (interaction.type === '生' || interaction.type === '克')) {
      points.push(`${lineRefWithRole(source)}${activity}${interaction.type}世爻${lineRef(target)}。`);
    }
  }

  // 日辰冲静爻且同五行比扶时，只在证据语言中组织为暗动作用；
  // 不改 analyze() 的历史 marks/score，因而 heuristicScore 回归锚点不变。
  const dayBranch = result.pillars.日.at(-1);
  const dayElement = BRANCH_ELEMENT[dayBranch];
  for (const source of result.yao.filter((line) =>
    line.isStaticClashedByDay
      && !line.marks.includes('暗动')
      && dayElement === line.element)) {
    for (const target of result.yao) {
      if (target.position === source.position) continue;
      const type = generates(source.element, target.element) ? '生'
        : overcomes(source.element, target.element) ? '克' : null;
      if (!type) continue;
      points.push(
        `${lineRefWithRole(source)}静而逢日辰${result.pillars.日}冲，`
        + `得日辰${result.pillars.日}${dayElement}比扶为暗动，`
        + `暗动${type}${lineRefWithRole(target)}。`
      );
    }
  }

  const calendarGroups = new Map();
  for (const support of result.calendarSupport) {
    const key = `${support.source}${support.type}`;
    const targets = calendarGroups.get(key) || [];
    targets.push(lineRef(result.yao[support.targetPosition - 1]));
    calendarGroups.set(key, targets);
  }
  if (calendarGroups.size) {
    points.push(
      `日月作用事实：${[...calendarGroups].map(([key, targets]) => `${key}${[...new Set(targets)].join('、')}`).join('；')}。`
    );
  }

  // 静爻虽不构成 analyze().interactions，六亲/五行/世应关系仍是盘上可核事实。
  const world = result.yao.find(({ isShi }) => isShi);
  const response = result.yao.find(({ isYing }) => isYing);
  if (world && response) {
    if (generates(world.element, response.element)) {
      points.push(`${lineRefWithRole(world)}生${lineRefWithRole(response)}，世爻生应爻。`);
    } else if (generates(response.element, world.element)) {
      points.push(`${lineRefWithRole(response)}生${lineRefWithRole(world)}，应爻生世爻。`);
    }
    if (combines(world.branch, response.branch)) {
      points.push(`${lineRefWithRole(world)}与${lineRefWithRole(response)}地支六合，世应相合。`);
    }
  }

  for (const line of result.yao) {
    if (line.position === useLine.position) continue;
    if (generates(line.element, useLine.element)) {
      points.push(`${lineRefWithRole(line)}五行生用神${lineRef(useLine)}。`);
    } else if (overcomes(line.element, useLine.element)) {
      points.push(`${lineRefWithRole(line)}五行克用神${lineRef(useLine)}。`);
    }
    if (world && line.transform && combines(line.transform.toBranch, world.branch)) {
      points.push(
        `${lineRefWithRole(line)}发动变${line.transform.toRelative}${line.transform.to}，`
        + `变爻${line.transform.toBranch}与世爻${world.branch}六合。`
      );
    }
  }

  const movingText = result.moving.length
    ? result.moving.map((position) => lineRef(result.yao[position - 1])).join('、')
    : `${lineRef(useLine)}所在卦六爻安静`;
  points.push(
    `卦体事实：以${lineRef(useLine)}为用神断点，本卦${result.primary.name}`
    + `${result.changed ? `，${movingText}发动后变${result.changed.name}` : `，${movingText}`}。`
  );
  return points;
}

function buildVerdictFacts(result, useGod, useLine, auxiliaries, selection, hiddenInfo, relations) {
  return {
    用神: {
      relative: useGod.用神,
      element: useGod.用神五行 || useLine.element,
      isAbsent: Boolean(hiddenInfo),
      primary: lineEvidence(useLine),
      auxiliaries: auxiliaries.map(lineEvidence),
      selectionReason: selection?.reason || null,
      flying: hiddenInfo ? lineEvidence(hiddenInfo.flying) : null
    },
    受克: relations.overcome,
    得生: relations.generated,
    作用关系: result.interactions.map((interaction) => ({ ...interaction })),
    爻间六合: (result.lineCombinations || []).map((combination) => ({ ...combination })),
    五行分布: result.elementPresence ? { ...result.elementPresence } : null,
    特殊状态: specialStates(useLine),
    卦象: [...result.patterns],
    复合事实: result.compoundFacts.map((fact) => ({ ...fact })),
    三合局关系: result.tripleCombineRelations.map((relation) => ({ ...relation }))
  };
}

/**
 * @param {ReturnType<import('./analyze.js').analyze>} result
 * 本函数只组织证据，不给吉凶结论。heuristicScore 是旧线性模型的回归锚点，
 * 已经由开发集证明不适合作为方向判断。
 */
export function verdict(result) {
  if (!result?.useGod) throw new TypeError('verdict() 需要 analyze() 已指定用神的结果');
  if (!Array.isArray(result.yao) || result.yao.length !== 6) {
    throw new TypeError('verdict() 需要 analyze() 的六爻结果');
  }

  const useGod = result.useGod;
  let main;
  let auxiliaries = [];
  let selection = null;
  let hiddenInfo = null;

  if (useGod.伏藏) {
    if (!useGod.伏神) throw new TypeError('用神伏藏但 analyze() 未给出伏神');
    const flying = result.yao.find(({ position }) => position === useGod.伏神.position);
    if (!flying) throw new TypeError('伏神对应的飞神爻位不存在');
    main = flying;
    hiddenInfo = {
      flying,
      relation: flyingRelation(useGod.伏神, flying),
      release: hiddenRelease(result, useGod.伏神, flying)
    };
    hiddenInfo.outcome = hiddenOutcome(useGod.伏神, hiddenInfo.relation, hiddenInfo.release);
  } else {
    const candidates = useGod.主爻 || [];
    if (!candidates.length) throw new TypeError('analyze() 结果没有可引用的用神爻');
    selection = selectMainUseGod(candidates);
    main = selection.main;
    auxiliaries = selection.auxiliaries;
  }

  const useElement = useGod.用神五行 || main.element;
  const weights = [
    ...patternWeights(result, useElement),
    ...punishmentWeights(main)
  ];
  const patternTotal = weights.reduce((sum, { weight }) => sum + weight, 0);
  const hiddenTotal = hiddenInfo
    ? weightValue(HIDDEN_WEIGHT_IDS.用神不上卦)
      + hiddenInfo.relation.weight
      + (hiddenInfo.release.canRelease ? weightValue(HIDDEN_WEIGHT_IDS.伏神可出伏) : 0)
    : 0;
  const baseStrength = hiddenInfo ? hiddenTotal : main.score;
  const heuristicScore = round(baseStrength + patternTotal);

  let takeUseGod;
  if (hiddenInfo) {
    takeUseGod = `用神${useGod.用神}不上卦，取伏神；伏于${lineRef(main)}之下。`;
  } else if (auxiliaries.length) {
    takeUseGod = `用神两现，取${lineRef(main)}为主（${selection.reason}），${auxiliaries.map(lineRef).join('、')}为辅。`;
  } else {
    takeUseGod = `取${useGod.用神}为用神，落在${lineRef(main)}。`;
  }

  const useLine = hiddenInfo
    ? { ...useGod.伏神, state: hiddenInfo.release.state, marks: useGod.伏神.marks || [] }
    : main;
  const strengthText = hiddenInfo
    ? `${lineRef(main)}之下的伏神${lineRef(useLine)}在${result.pillars.月.at(-1)}月为${hiddenInfo.release.state}；${dayRelation(useLine, result.pillars.日)}；${hiddenInfo.relation.text}。`
    : `${lineRefWithRole(main)}旺衰为${main.state}（${marksText(main)}）。`;
  const relationText = hiddenInfo
    ? `${calendarInfluenceText(result, { ...useGod.伏神, state: hiddenInfo.release.state, marks: [] }, '伏神', false)}${lineRef(main)}为飞神；${hiddenInfo.outcome}。`
    : interactionText(result, main, useGod);

  const relations = relationFacts(result, useLine, hiddenInfo);
  const states = specialStates(useLine);
  const keyPoints = [
    calendarKeyPoint(result, useLine, hiddenInfo ? '伏神' : '用神'),
    ...relationKeyPoints(result, relations, useGod),
    ...compoundKeyPoints(result),
    ...generalFactKeyPoints(result, useLine),
    ...missingElementKeyPoints(result, useLine),
    ...lineCombinationKeyPoints(result),
    ...retreatTimingKeyPoints(result),
    ...timingKeyPoints(result)
  ];
  if (states.length) keyPoints.push(`${hiddenInfo ? '伏神' : '用神'}${lineRef(useLine)}特殊状态：${states.join('、')}。`);
  const patternPoint = patternKeyPoint(result, useLine);
  if (patternPoint) keyPoints.push(patternPoint);

  return {
    chain: [
      { step: '取用神', text: takeUseGod },
      { step: '断旺衰', text: strengthText },
      { step: '看生克', text: relationText },
      { step: '察动变', text: transformText(result, main) }
    ],
    facts: buildVerdictFacts(result, useGod, useLine, auxiliaries, selection, hiddenInfo, relations),
    keyPoints: [...new Set(keyPoints)],
    heuristicScore,
    heuristicScoreNote: HEURISTIC_NOTE,
    level: null,
    note: VERDICT_NOTE
  };
}

/**
 * 返回本次 verdict() 实际触发的权重 id/value，供开发集溯源统计使用。
 * 不改变 verdict() 的公开输出，也不判断某一条权重是否“导致”最终方向。
 */
export function verdictWeightContributions(result) {
  if (!result?.useGod || !Array.isArray(result.yao) || result.yao.length !== 6) {
    throw new TypeError('verdictWeightContributions() 需要已指定用神的 analyze() 结果');
  }

  let main;
  let base = [];
  if (result.useGod.伏藏) {
    const hidden = result.useGod.伏神;
    main = result.yao.find(({ position }) => position === hidden?.position);
    if (!hidden || !main) throw new TypeError('伏神或对应飞神缺失');
    const relation = flyingRelation(hidden, main);
    const release = hiddenRelease(result, hidden, main);
    base.push(contribution(HIDDEN_WEIGHT_IDS.用神不上卦, '用神不上卦'));
    if (relation.id) base.push(contribution(relation.id, relation.text));
    if (release.canRelease) base.push(contribution(HIDDEN_WEIGHT_IDS.伏神可出伏, '伏神可出伏'));
  } else {
    const selection = selectMainUseGod(result.useGod.主爻 || []);
    main = selection.main;
    if (!main) throw new TypeError('analyze() 结果没有可引用的用神爻');
    base = (main.scoreWeights || []).map(({ id, value }) => ({
      id, pattern: `主用神${lineRef(main)}`, weight: value
    }));
  }

  const useElement = result.useGod.用神五行 || main.element;
  return [
    ...base,
    ...patternWeights(result, useElement),
    ...punishmentWeights(main)
  ].map(({ id, pattern, weight }) => ({ id, pattern, value: weight }));
}
